/**
 * TAZZIEDEVIL2.0 — Tenacious Autonomous Zero-Zone Intelligence Engine
 * The AI chatbot for every Anchor sub-species platform.
 * Named, branded, and sector-aware — each deployment knows its domain.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { SectorMarketingConfig } from './SectorMarketingData';

// ─── Types ───────────────────────────────────────────────────────────
interface TazzieDevilMessage {
  id: string;
  role: 'user' | 'tazziedevil';
  content: string;
  timestamp: number;
  isStreaming?: boolean;
  category?: 'general' | 'security' | 'intelligence' | 'support' | 'demo';
}

interface TazzieDevil2Props {
  config: SectorMarketingConfig;
  isDemo?: boolean;
  onNavigate?: (view: string) => void;
}

// ─── Sector-Aware Knowledge Base ─────────────────────────────────────
function getSectorKnowledge(config: SectorMarketingConfig): Record<string, string> {
  const base: Record<string, string> = {
    'what is tazziedevil': `I'm TAZZIEDEVIL2.0 — Tenacious Autonomous Zero-Zone Intelligence Engine. I'm the AI assistant powering ${config.brandName}. I can help you navigate the platform, explain features, answer security questions, and guide you through ${config.id} intelligence operations.`,
    'who are you': `I'm TAZZIEDEVIL2.0, your dedicated AI assistant for ${config.brandName}. I'm built on the same self-evolving AI engine that powers all 10 Anchor Intelligence platforms. Think of me as your personal ${config.id} intelligence analyst, available 24/7.`,
    help: `Here's what I can help you with:\n\n🔍 **Intelligence Queries** — Ask about ${config.id} data, trends, and signals\n📊 **Platform Navigation** — I'll guide you to any feature or dashboard\n🛡️ **Security** — Questions about data protection and compliance\n💡 **Features** — Detailed explanations of ${config.brandName} capabilities\n📋 **Reports** — Generate intelligence summaries on demand\n🔧 **Support** — Troubleshooting and account management`,
    pricing: `${config.brandName} offers ${(config.pricing ?? []).length} plans:\n\n${(config.pricing ?? []).map((p) => `**${p.name}** — ${p.price}${p.period} (${p.features.length} features included)`).join('\n')}.

Business plans use guided demos; production access requires a paid subscription.`,
    features: `${config.brandName} has ${config.features.length} core capabilities:\n\n${config.features.map((f) => `${f.icon} **${f.title}**${f.worldFirst ? ' 🏆 WORLD FIRST' : ''} — ${f.description.substring(0, 100)}...`).join('\n\n')}`,
    'data sources': `${config.brandName} connects to ${config.dataSources.length} authoritative intelligence sources:\n\n${config.dataSources.map((ds) => `📡 **${ds.name}** — ${ds.description}`).join('\n')}`,
    api: `${config.brandName} offers ${config.apiProducts.length} developer API products:\n\n${config.apiProducts.map((a) => `\`${a.endpoint}\` — ${a.name} (${a.pricePerCall}/call)`).join('\n')}`,
    company: `${config.companyStructure.entity}\n📍 ${config.companyStructure.hq}\n👥 ${config.companyStructure.headcount}\n🏢 ${config.companyStructure.division}\n\nRegistrations: ${config.companyStructure.registrations.join(', ')}`,
    demo: `You're currently in demo mode! You have full read-only access to explore ${config.brandName}. Here's what you can do:\n\n✅ Browse all intelligence dashboards\n✅ Explore features and capabilities\n✅ Ask me anything about ${config.id} intelligence\n✅ View sample reports and data\n\nTo unlock full capabilities, create a free account with your email.`,
    security: `${config.brandName} is built on the same sovereign-grade security infrastructure as Anchor AI Guard — the world's most comprehensive cybersecurity platform.\n\n🔒 End-to-end encryption (AES-256)\n🛡️ SOC 2 Type II certified\n🏛️ Air-gapped deployment available\n🌐 GDPR, CCPA, and Australian Privacy Act compliant\n🔑 SSO via SAML/OIDC\n📋 99.9%+ SLA guaranteed`,
    partners: `${config.brandName} integrates with ${config.technologyPartners.length} strategic technology partners:\n\n${config.technologyPartners.map((p) => `${p.logo} **${p.name}** — ${p.integration.substring(0, 80)}...`).join('\n\n')}`,
    revenue: `${config.brandName} operates ${(config.revenueStreams ?? []).length} revenue engines:\n\n${(config.revenueStreams ?? []).map((r) => `${r.icon} **${r.name}** — ${r.annualPotential}/yr projected`).join('\n')}`,
  };
  return base;
}

function findBestResponse(query: string, config: SectorMarketingConfig): string {
  const knowledge = getSectorKnowledge(config);
  const q = query.toLowerCase().trim();

  // Direct keyword matching
  for (const [key, response] of Object.entries(knowledge)) {
    if (q.includes(key)) return response;
  }

  // Intent detection
  if (q.match(/pric|cost|plan|subscri|how much|pay/)) return knowledge['pricing'];
  if (q.match(/feature|capabilit|what can|do you/)) return knowledge['features'];
  if (q.match(/data|source|connect|integrat|feed/)) return knowledge['data sources'];
  if (q.match(/api|endpoint|developer|sdk/)) return knowledge['api'];
  if (q.match(/secur|encrypt|compli|gdpr|soc|protect/)) return knowledge['security'];
  if (q.match(/partner|elon|musk|tesla|spacex|starlink|neuralink|xai|grok|boring/))
    return knowledge['partners'];
  if (q.match(/revenue|monetiz|business model|income/)) return knowledge['revenue'];
  if (q.match(/company|entity|team|headcount|office|hq/)) return knowledge['company'];
  if (q.match(/demo|trial|test|sample/)) return knowledge['demo'];
  if (q.match(/help|support|how|guide|start/)) return knowledge['help'];
  if (q.match(/who|what is|tazzie|devil|about/)) return knowledge['who are you'];

  // Sector-specific contextual response
  return `Great question! As your ${config.brandName} intelligence assistant, I can help with that.\n\nBased on our ${config.dataSources.length} connected data sources and ${config.features.length} AI-powered capabilities, here's what I know:\n\n${config.solutionStatement}\n\nWould you like me to dive deeper into any specific area? Try asking about:\n• Features and capabilities\n• Data sources and integrations\n• Technology partnerships\n• Pricing and plans\n• Security and compliance`;
}

// ─── TAZZIEDEVIL2.0 Quick Actions ────────────────────────────────────
function getSectorQuickActions(config: SectorMarketingConfig): string[] {
  const base = [
    `What can ${config.brandName} do?`,
    'Show me your data sources',
    'What are the pricing plans?',
    'Tell me about security',
  ];
  const sectorSpecific: Record<string, string[]> = {
    medical: [
      'Show me drug interaction data',
      'What clinical trials are active?',
      'Explain adverse event detection',
    ],
    finance: [
      'Show me market anomalies',
      'What regulatory filings are new?',
      'Explain cross-asset correlation',
    ],
    legal: [
      'Track regulatory changes',
      'How does contract review work?',
      'Show me case law analysis',
    ],
    climate: ['Show me carbon data', 'What physical risks exist?', 'Explain ESG reporting'],
    defence: [
      'Show me threat assessment',
      'How does adversary prediction work?',
      'Explain multi-domain fusion',
    ],
    energy: [
      'Show me grid status',
      'What supply disruptions are predicted?',
      'Explain commodity forecasting',
    ],
    maritime: [
      'Show me vessel tracking',
      'What ports are congested?',
      'Explain sanctions screening',
    ],
    space: [
      'Show me orbital tracking',
      'What conjunctions are predicted?',
      'Explain debris analysis',
    ],
    agriculture: ['Show me crop health data', 'What pest risks exist?', 'Explain yield estimation'],
    education: [
      'Show me skills gap data',
      'How does curriculum analysis work?',
      'Explain student prediction',
    ],
  };
  return [...base, ...(sectorSpecific[config.id] || [])];
}

// ─── Component ───────────────────────────────────────────────────────
const TazzieDevil2Chat: React.FC<TazzieDevil2Props> = ({
  config,
  isDemo = false,
  onNavigate: _onNavigate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<TazzieDevilMessage[]>([
    {
      id: 'welcome',
      role: 'tazziedevil',
      content: `👋 Welcome to ${config.brandName}! I'm **TAZZIEDEVIL2.0** — your dedicated ${config.id} intelligence assistant.\n\n${isDemo ? "🎮 You're in demo mode — explore everything freely!\n\n" : ''}Ask me anything about ${config.id} intelligence, platform features, or security. I'm here 24/7.`,
      timestamp: Date.now(),
      category: 'general',
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const quickActions = getSectorQuickActions(config);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(
    (text: string) => {
      if (!text.trim()) return;

      const userMsg: TazzieDevilMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text.trim(),
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setIsTyping(true);

      // Simulate TAZZIEDEVIL2.0 processing (would be real API in production)
      setTimeout(
        () => {
          const response = findBestResponse(text, config);
          const tazzieMsg: TazzieDevilMessage = {
            id: `tazziedevil-${Date.now()}`,
            role: 'tazziedevil',
            content: response,
            timestamp: Date.now(),
            category: 'intelligence',
          };
          setMessages((prev) => [...prev, tazzieMsg]);
          setIsTyping(false);
        },
        800 + Math.random() * 1200
      );
    },
    [config]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Render markdown-lite (bold, code, newlines)
  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      const parts = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={j} className="text-white font-semibold">
              {part.slice(2, -2)}
            </strong>
          );
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code
              key={j}
              className="bg-white/10 text-cyan-300 px-1.5 py-0.5 rounded text-xs font-mono"
            >
              {part.slice(1, -1)}
            </code>
          );
        }
        return <span key={j}>{part}</span>;
      });
      return (
        <div key={i} className={line === '' ? 'h-2' : ''}>
          {parts}
        </div>
      );
    });
  };

  if (!isOpen) {
    // Floating chat button
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-linear-to-r ${config.colorFrom} ${config.colorTo} shadow-lg shadow-${config.accentColor}-500/30 flex items-center justify-center hover:scale-110 transition-all group`}
        title="Chat with TAZZIEDEVIL2.0"
      >
        <div className="relative">
          <span className="text-2xl">🐾</span>
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-slate-950 animate-pulse" />
        </div>
        <div className="absolute -top-10 right-0 bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-white/10">
          Chat with TAZZIEDEVIL2.0
        </div>
      </button>
    );
  }

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className={`w-64 bg-slate-900 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3 hover:border-white/20 transition-colors shadow-xl`}
        >
          <span className="text-xl">🐾</span>
          <div className="flex-1 text-left">
            <div className="text-sm font-semibold text-white">TAZZIEDEVIL2.0</div>
            <div className="text-[10px] text-slate-500">{config.brandName} Assistant</div>
          </div>
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-100 h-150 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
      {/* Header */}
      <div
        className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} px-4 py-3 flex items-center gap-3`}
      >
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
          <span className="text-xl">🐾</span>
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-white flex items-center gap-2">
            TAZZIEDEVIL2.0
            <span className="bg-white/20 text-[9px] px-1.5 py-0.5 rounded-full font-medium">
              AI
            </span>
          </div>
          <div className="text-[10px] text-white/70">
            {config.brandName} Intelligence Assistant &bull; Online
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(true)}
            className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 transition-colors"
            title="Minimize"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 transition-colors"
            title="Close"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] ${msg.role === 'user' ? 'bg-linear-to-r ' + config.colorFrom + ' ' + config.colorTo + ' text-white' : 'bg-slate-800 text-slate-200'} rounded-xl px-4 py-3`}
            >
              {msg.role === 'tazziedevil' && (
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-xs">🐾</span>
                  <span className="text-[10px] font-bold text-white/60 uppercase tracking-wider">
                    TAZZIEDEVIL2.0
                  </span>
                </div>
              )}
              <div className="text-sm leading-relaxed">{renderContent(msg.content)}</div>
              <div
                className={`text-[9px] mt-1.5 ${msg.role === 'user' ? 'text-white/50' : 'text-slate-500'}`}
              >
                {new Date(msg.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-slate-800 rounded-xl px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-xs">🐾</span>
                <span className="text-[10px] font-bold text-white/60 uppercase tracking-wider">
                  TAZZIEDEVIL2.0
                </span>
              </div>
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" />
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-medium">
            Quick Actions
          </div>
          <div className="flex flex-wrap gap-1.5">
            {quickActions.slice(0, 6).map((action, i) => (
              <button
                key={i}
                onClick={() => sendMessage(action)}
                className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1 text-[11px] text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                {action}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-white/10 p-3 flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask TAZZIEDEVIL2.0 about ${config.id} intelligence...`}
          className="flex-1 bg-slate-800 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/20 resize-none"
          rows={1}
        />
        <button
          type="submit"
          disabled={!input.trim() || isTyping}
          className={`w-9 h-9 rounded-xl bg-linear-to-r ${config.colorFrom} ${config.colorTo} flex items-center justify-center text-white hover:opacity-90 transition-opacity disabled:opacity-30`}
          title="Send message"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </form>

      {/* Footer */}
      <div className="px-3 pb-2 flex items-center justify-between">
        <span className="text-[9px] text-slate-600">
          TAZZIEDEVIL2.0 &bull; Powered by Anchor AI Guard
        </span>
        {isDemo && <span className="text-[9px] text-amber-500/60 font-medium">DEMO MODE</span>}
      </div>
    </div>
  );
};

export default TazzieDevil2Chat;
